import {
  Box,
  Checkbox,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Icon,
  Input,
  Tooltip,
  VStack,
} from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { MdCheckCircle } from "react-icons/md";

import { Button, SettingsPageContainer, useToast } from "../../../components";
import { useBooleanSearchParam } from "../../../hooks/useSearchParam";
import {
  CurrentUserFragment,
  useUpdateOrganizationMsftTeamsSettingsMutation,
} from "../../graphql";

interface MsftTeamsChatNotificationsSettingsProps {
  currentUser: CurrentUserFragment;
}

interface FormData {
  tenantId: string;
  shouldSendWelcomeMessage: boolean;
  shouldSendChatReminder: boolean;
  autoInstallEnabled: boolean;
  allNotificationsEnabled: boolean;
}

const MsftTeamsChatNotificationsSettings: React.FC<
  MsftTeamsChatNotificationsSettingsProps
> = ({ currentUser }) => {
  const toast = useToast();
  const settings = currentUser?.organization.teamsAppSettings;
  const [adminConsentUrl, setAdminConsentUrl] = useState<string | undefined>(
    settings?.adminConsentUrl ?? undefined
  );
  const [adminConsentQueryParam] = useBooleanSearchParam("admin_consent");
  const [autoInstallAdminConsent, setAutoInstallAdminConsent] =
    useState<boolean>(settings?.autoInstallAdminConsent ?? false);
  const autoOpenTooltip = !adminConsentUrl
    ? "Enter the Tenant ID to enable this action"
    : "Configure auto install for your teams meetings";

  const {
    register,
    handleSubmit,
    formState: { isSubmitting, errors },
  } = useForm<FormData>({
    defaultValues: {
      tenantId: settings?.tenantId || "",
      shouldSendWelcomeMessage: settings?.shouldSendWelcomeMessage ?? true,
      shouldSendChatReminder: settings?.shouldSendChatReminder ?? true,
      autoInstallEnabled: settings?.autoInstallEnabled ?? true,
      allNotificationsEnabled: settings?.allNotificationsEnabled ?? false,
    },
  });

  const [updateMsftTeamsSettings] =
    useUpdateOrganizationMsftTeamsSettingsMutation({
      onCompleted: (data) => {
        toast({
          status: "success",
          title: "Settings Updated",
          description:
            "Microsoft Teams settings have been successfully updated.",
        });
        setAdminConsentUrl(
          data.updateOrganizationMsftTeamsSettings?.organization
            ?.teamsAppSettings?.adminConsentUrl ?? undefined
        );
        setAutoInstallAdminConsent(
          data.updateOrganizationMsftTeamsSettings?.organization
            ?.teamsAppSettings?.autoInstallAdminConsent ?? false
        );
      },
      onError: (error) => {
        toast({
          status: "error",
          title: "Error",
          description:
            error.message || "Failed to update Microsoft Teams settings.",
        });
      },
    });

  useEffect(() => {
    if (adminConsentQueryParam) {
      // If the redirection from msft comes with the `admin_consent=True` querystring
      // parameter, save it to the DB.
      handleSubmit((formData) => {
        updateMsftTeamsSettings({
          variables: { ...formData, autoInstallAdminConsent: true },
        });
        // Clear the query parameter after executing the mutation
        const url = new URL(window.location.href);
        url.search = "";
        window.history.replaceState({}, "", url);
      })();
    }
  }, [adminConsentQueryParam, handleSubmit, updateMsftTeamsSettings]);

  return (
    <>
      <SettingsPageContainer
        heading="Microsoft Teams App Settings"
        subHeading="Configure Microsoft Teams integration settings for your organization."
      >
        <Box
          as="form"
          onSubmit={handleSubmit((formData) => {
            updateMsftTeamsSettings({ variables: formData });
          })}
          maxW="500px"
        >
          <VStack spacing={6} align="stretch">
            <FormControl isInvalid={!!errors.tenantId}>
              <FormLabel>Tenant ID</FormLabel>
              <Input
                {...register("tenantId", {})}
                placeholder="Enter your Microsoft Teams tenant ID"
                maxLength={60}
              />
              {errors?.tenantId && (
                <FormErrorMessage>{errors.tenantId.message}</FormErrorMessage>
              )}
            </FormControl>

            <FormControl>
              <Checkbox {...register("shouldSendWelcomeMessage")}>
                Send welcome message when joining Teams
              </Checkbox>
            </FormControl>

            <FormControl>
              <Checkbox {...register("shouldSendChatReminder")}>
                Send in meeting chat message that reminds interviewers to open
                the BrightHire Interview Assistant teams app
              </Checkbox>
            </FormControl>

            {autoInstallAdminConsent && (
              <FormControl>
                <Checkbox {...register("autoInstallEnabled")}>
                  Add interview assistant to meetings automatically
                </Checkbox>
              </FormControl>
            )}

            <FormControl>
              <Checkbox {...register("allNotificationsEnabled")}>
                Enable BrightHire Interview Assistant to send Teams
                notifications to users
              </Checkbox>
            </FormControl>

            <Box>
              <Button
                type="submit"
                colorScheme="blue"
                isLoading={isSubmitting}
                loadingText="Saving..."
              >
                Save Settings
              </Button>
            </Box>
          </VStack>
        </Box>
      </SettingsPageContainer>
      <SettingsPageContainer
        mt={8}
        heading="Auto Install"
        subHeading="Allow the Interview Assistant to automatically be added to Microsoft Teams meetings."
      >
        <Box>
          {autoInstallAdminConsent && (
            <Box mb={8}>
              <Flex alignItems="center">
                <Icon
                  boxSize="18px"
                  mr={4}
                  as={MdCheckCircle}
                  color="green.600"
                />
                <Box fontSize="14">
                  Auto install has been enabled for your organization.
                </Box>
              </Flex>
            </Box>
          )}
          <Tooltip label={autoOpenTooltip}>
            <Button
              type="submit"
              onClick={() => {
                if (typeof adminConsentUrl === "string") {
                  window.location.href = adminConsentUrl;
                }
              }}
              disabled={!adminConsentUrl}
            >
              {autoInstallAdminConsent
                ? "Reconfigure Auto Install"
                : "Configure Auto Install"}
            </Button>
          </Tooltip>
        </Box>
      </SettingsPageContainer>
    </>
  );
};

export default MsftTeamsChatNotificationsSettings;
